import React, { useState, useEffect } from "react";
import { observer, useObserver } from "mobx-react";
import { v4 as uuid } from "uuid";
import { Tooltip } from "antd";
import { NodeViewWrapper } from "@tiptap/react";
import cn from "classnames";

import commentStore from "@app/state/store/report-document/comment";

import "./style.scss";

export default observer(({ editor, node, extension, getPos }) => {
    const store = extension.storage.store;
    const readonly = extension.options.readonly;
    const nodeId = useState(uuid());
    const { comment } = node.attrs;
    const isHighlighted = comment && comment.includes(commentStore.focusedComment);
    const status =
        commentStore.focusedComment &&
        commentStore.comments.find((c) => c._id === commentStore.focusedComment)?.status;

    const { templateMode } = editor.view._props;

    // re-render the component upon store change
    useObserver(() => store.entries);

    useEffect(() => {
        if (!editor) {
            return;
        }

        editor.on("removeDictionary", remove);
        return () => editor.off("removeDictionary", remove);
    }, [editor]);

    // do not render the node in case if missing id
    const key = node.attrs.key;
    const change = node.attrs?.["data-diff-node"];
    const diffId = node.attrs?.["data-diff-id"];

    if (!key) {
        return null;
    }

    // do not render the node if we don't have a dictionary entry
    const entry = store.use(key, nodeId);
    const isTemplateValue = templateMode || !entry?.text;
    const invalid = !entry;

    /**
     * Remove the entry
     */
    function remove({ node }) {
        if (node.attrs.key === key) {
            const from = getPos();
            const to = from + node.nodeSize;

            editor.chain().deleteRange({ from, to }).focus().run();
        }
    }

    const ChangeWrapper = ({ children, change }) => {
        if (change === "inserted") {
            return (
                <ins
                    data-diff-id={diffId}
                    dangerouslySetInnerHTML={{
                        __html: children,
                    }}
                ></ins>
            );
        }

        if (change === "deleted") {
            return (
                <del
                    data-diff-id={diffId}
                    dangerouslySetInnerHTML={{
                        __html: children,
                    }}
                ></del>
            );
        }

        if (change === "changed") {
            return (
                <chg
                    data-diff-id={diffId}
                    dangerouslySetInnerHTML={{
                        __html: children,
                    }}
                ></chg>
            );
        }

        return (
            <span
                dangerouslySetInnerHTML={{
                    __html: children,
                }}
            ></span>
        );
    };

    const Entry = observer(({ entry, change }) => {
        return (
            <Tooltip title={entry.name} placement="top" mouseEnterDelay={0.3}>
                <span>
                    <ChangeWrapper change={change}>{entry.text || entry.name}</ChangeWrapper>
                </span>
            </Tooltip>
        );
    });

    return (
        <>
            {invalid && (
                <NodeViewWrapper
                    className={cn({
                        "dictionary-entry": true,
                        invalid: true,
                        "template-mode": isTemplateValue,
                        pending: isHighlighted && status === "pending",
                        resolved: isHighlighted && status === "resolved",
                    })}
                >
                    {key}
                </NodeViewWrapper>
            )}

            {!invalid && !readonly && (
                <NodeViewWrapper
                    className={cn({
                        "dictionary-entry": true,
                        "template-mode": isTemplateValue,
                        pending: isHighlighted && status === "pending",
                        resolved: isHighlighted && status === "resolved",
                    })}
                >
                    <Entry entry={entry} change={change} />
                </NodeViewWrapper>
            )}

            {!invalid && readonly && (
                <NodeViewWrapper
                    className={cn({
                        "dictionary-entry": true,
                        "template-mode": isTemplateValue,
                        pending: isHighlighted && status === "pending",
                        resolved: isHighlighted && status === "resolved",
                    })}
                >
                    <Entry entry={entry} change={change} />
                </NodeViewWrapper>
            )}
        </>
    );
});
